export const MONTH_LABELS = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
] as const

export const MONTH_LABELS_FULL = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
] as const

export function shortToFullMonth(short: string): string {
	const index = (MONTH_LABELS as readonly string[]).indexOf(short)
	return index >= 0 ? MONTH_LABELS_FULL[index] : short
}

export function formatYearMonthShort(month: string): string {
	return MONTH_LABELS[Number(month.slice(5, 7)) - 1] ?? month
}

export function formatYearMonthFull(month: string): string {
	return `${formatYearMonthShort(month)} ${month.slice(0, 4)}`
}

export function formatYearMonthCompact(month: string): string {
	return `${formatYearMonthShort(month)} ${month.slice(2, 4)}`
}

export function formatPercent(value: number): string {
	return `${Math.round(value * 100)}%`
}

export function formatSharePct(value: number): string {
	return `${value.toFixed(1)}%`
}

export function formatNumber(value: number, decimals = 2): string {
	return new Intl.NumberFormat("en-US", {
		minimumFractionDigits: decimals,
		maximumFractionDigits: decimals,
	}).format(value)
}

export function formatDate(dateStr: string): string {
	const date = parseUTCDate(dateStr)
	const day = date.getUTCDate()
	const month = MONTH_LABELS[date.getUTCMonth()]
	const year = date.getUTCFullYear().toString().slice(-2)
	return `${day} ${month} ${year}`
}

export function formatDateDelimited(
	dateStr: string,
	delimiter = "-",
	fullYear = false,
): string {
	const date = parseUTCDate(dateStr)
	const dd = String(date.getUTCDate()).padStart(2, "0")
	const mm = String(date.getUTCMonth() + 1).padStart(2, "0")
	const year = fullYear
		? String(date.getUTCFullYear())
		: date.getUTCFullYear().toString().slice(-2)
	return `${dd}${delimiter}${mm}${delimiter}${year}`
}

export const formatDateShort = (dateStr: string) =>
	formatDateDelimited(dateStr, "-")
export const formatDateSlash = (dateStr: string) =>
	formatDateDelimited(dateStr, "/")

export function formatTimestampShort(dateStr: string): string {
	const date = parseUTCDate(dateStr)
	const dd = String(date.getUTCDate()).padStart(2, "0")
	const mm = String(date.getUTCMonth() + 1).padStart(2, "0")
	const yy = date.getUTCFullYear().toString().slice(-2)
	const hh = String(date.getUTCHours()).padStart(2, "0")
	const min = String(date.getUTCMinutes()).padStart(2, "0")
	return `${dd}-${mm}-${yy} ${hh}:${min}`
}

export function formatDateISO(date: Date): string {
	const yyyy = date.getFullYear()
	const mm = String(date.getMonth() + 1).padStart(2, "0")
	const dd = String(date.getDate()).padStart(2, "0")
	return `${yyyy}-${mm}-${dd}`
}

export function formatDateISOUTC(date: Date): string {
	const yyyy = date.getUTCFullYear()
	const mm = String(date.getUTCMonth() + 1).padStart(2, "0")
	const dd = String(date.getUTCDate()).padStart(2, "0")
	return `${yyyy}-${mm}-${dd}`
}

export function todayISO(): string {
	return formatDateISO(new Date())
}

export function parseDateISO(dateString: string | undefined): Date | undefined {
	if (!dateString) return undefined
	const [y, m, d] = dateString.split("-").map(Number)
	return new Date(y, m - 1, d)
}

function isLeapYear(year: number): boolean {
	return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
}

export function dayOfYearToDate(dayOfYear: number, year: number): Date {
	const daysInYear = isLeapYear(year) ? 366 : 365
	return new Date(year, 0, Math.min(Math.max(dayOfYear, 1), daysInYear))
}

export function dayOfYearToMonth(dayOfYear: number, year: number): string {
	return MONTH_LABELS[dayOfYearToDate(dayOfYear, year).getMonth()] ?? ""
}

export function formatDayOfYear(dayOfYear: number, year: number): string {
	const date = dayOfYearToDate(dayOfYear, year)
	return `${MONTH_LABELS[date.getMonth()]} ${date.getDate()}`
}

export function formatDayMonth(dateStr: string): string {
	const date = parseUTCDate(dateStr)
	return `${date.getUTCDate()} ${MONTH_LABELS[date.getUTCMonth()]}`
}

export function parseUTCDate(dateStr: string): Date {
	if (dateStr.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(dateStr)) {
		return new Date(dateStr)
	}
	return new Date(`${dateStr}Z`)
}

export function getDaysOld(dateStr: string): number {
	const date = parseUTCDate(dateStr)
	const utcDate = Date.UTC(
		date.getUTCFullYear(),
		date.getUTCMonth(),
		date.getUTCDate(),
	)
	const now = new Date()
	const utcNow = Date.UTC(
		now.getUTCFullYear(),
		now.getUTCMonth(),
		now.getUTCDate(),
	)
	return Math.floor((utcNow - utcDate) / (1000 * 60 * 60 * 24))
}

function sigFigs(value: number, figs = 3): string {
	if (value === 0) return "0"
	return String(parseFloat(value.toPrecision(figs)))
}

export function formatCompactNumber(value: number): string {
	if (Math.abs(value) >= 1_000_000) return `${sigFigs(value / 1_000_000)}M`
	if (Math.abs(value) >= 1_000) return `${sigFigs(value / 1_000)}K`
	return formatNumber(value, 0)
}

export function formatCompactCurrency(value: number): string {
	if (Math.abs(value) >= 1_000_000)
		return `\u20AC${sigFigs(value / 1_000_000)}M`
	if (Math.abs(value) >= 1_000) return `\u20AC${sigFigs(value / 1_000)}K`
	return `\u20AC${formatNumber(value, 0)}`
}

export function formatInteger(value: number): string {
	return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
		value,
	)
}

export function formatOptionalNumber(
	value: number | null | undefined,
	decimals = 2,
): string {
	if (value == null) return ""
	return formatNumber(value, decimals)
}

export const CURRENCY_SYMBOLS: Record<string, string> = {
	EUR: "\u20AC",
	USD: "$",
	GBP: "\u00A3",
	CHF: "CHF",
	NOK: "kr",
	SEK: "kr",
	DKK: "kr",
}

export function formatCurrencySymbol(currency: string): string {
	return CURRENCY_SYMBOLS[currency] ?? currency
}

export function formatDisplayName(
	name: string | null | undefined,
	email: string | null | undefined,
): string {
	if (name) return name
	if (!email) return ""
	return email
		.split("@")[0]
		.split(".")
		.map((w) => w.charAt(0).toUpperCase() + w.slice(1))
		.join(" ")
}

export function formatDateTime(value: string): string {
	if (!value) return "\u2014"
	const date = new Date(value)
	return date.toLocaleString("en-GB", {
		day: "numeric",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	})
}

export function humanizeEnum(
	value: string | null | undefined,
	options: { titleCase?: boolean } = {},
): string {
	if (!value) return ""
	const words = value.replace(/_/g, " ").trim().toLowerCase().split(/\s+/)
	if (options.titleCase) {
		return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")
	}
	if (words.length === 0) return ""
	const [first, ...rest] = words
	return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(" ")
}

export function formatEventDayLabel(
	value: string,
	now: Date = new Date(),
): string {
	const date = parseUTCDate(value)
	const startOfToday = new Date(
		now.getFullYear(),
		now.getMonth(),
		now.getDate(),
	).getTime()
	if (date.getTime() >= startOfToday) return "Today"
	if (date.getTime() >= startOfToday - 24 * 60 * 60 * 1000) return "Yesterday"
	const day = date.getDate()
	const month = MONTH_LABELS[date.getMonth()]
	if (date.getFullYear() === now.getFullYear()) return `${day} ${month}`
	return `${day} ${month} ${date.getFullYear()}`
}

export function formatEventTimeLabel(
	value: string,
	now: Date = new Date(),
): string {
	const date = parseUTCDate(value)
	const startOfToday = new Date(
		now.getFullYear(),
		now.getMonth(),
		now.getDate(),
	).getTime()
	if (date.getTime() >= startOfToday) {
		const diffMin = Math.floor((now.getTime() - date.getTime()) / 60_000)
		if (diffMin < 1) return "now"
		if (diffMin < 60) return `${diffMin}m`
		return `${Math.floor(diffMin / 60)}h`
	}
	const startOfEventDay = new Date(
		date.getFullYear(),
		date.getMonth(),
		date.getDate(),
	).getTime()
	const diffDays = Math.floor((startOfToday - startOfEventDay) / 86_400_000)
	if (diffDays < 7) return `${diffDays}d`
	return `${Math.floor(diffDays / 7)}w`
}

export function formatAbsoluteTimestamp(value: string): string {
	const date = parseUTCDate(value)
	const day = date.getDate()
	const month = MONTH_LABELS[date.getMonth()]
	const year = date.getFullYear()
	const hh = String(date.getHours()).padStart(2, "0")
	const mm = String(date.getMinutes()).padStart(2, "0")
	return `${day} ${month} ${year} · ${hh}:${mm}`
}

export function formatDuration(totalSeconds: number): string {
	const minutes = Math.floor(totalSeconds / 60)
	const seconds = totalSeconds % 60
	return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`
}

export function formatFileSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function sentenceCase(str: string): string {
	return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

export function formatRelativeTime(dateStr: string): string {
	const then = parseUTCDate(dateStr).getTime()
	const seconds = Math.floor((Date.now() - then) / 1000)
	if (seconds < 60) return "just now"
	const minutes = Math.floor(seconds / 60)
	if (minutes < 60) return `${minutes}m ago`
	const hours = Math.floor(minutes / 60)
	if (hours < 24) return `${hours}h ago`
	const days = Math.floor(hours / 24)
	if (days < 7) return `${days}d ago`
	const weeks = Math.floor(days / 7)
	if (weeks < 5) return `${weeks}w ago`
	const months = Math.floor(days / 30)
	if (months < 12) return `${months}mo ago`
	return `${Math.floor(days / 365)}y ago`
}
